/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { ProductDimension, ProductDimensionType } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertProductDimensionTypeToDimensionTypes,
    DimensionTypes,
    getDimensionValuesFromQuery,
    getInventoryLevelCodeFromDimensionValue,
    IDimensionForSelectedVariant,
    IDimensionsApp,
    IDimensionValueForSelectedVariant,
    InventoryLevelValues,
    ObjectExtensions,
    InventoryLevels
} from '@msdyn365-commerce-modules/retail-actions';
import { format, getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxCallbacks,
    IBuyboxCommonData,
    IBuyboxCommonResources,
    IBuyboxExtentedProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxState,
    IProductDetails,
    IDropdownItemProps,
    IDropdownOnSelectionChangeNotification,
    SelectionType
} from '@msdyn365-commerce-modules/buybox';
import memoize from 'memoize-one';
import { IHeiBuyboxCallbacks } from '../hei-buybox-v2.data';
import { Dropdown } from './dropdown';

let telemetryContent: ITelemetryContent;

/**
 * Dimension pre-processing info.
 */
interface IProductDimensionInfo {
    dropdownName: string;
    dropdownId: string;
    dropdownList: IDropdownItemProps[];
}

/**
 * Props for rendering a dropdown.
 */
interface IProductDimensionDropdownProps extends IProductDimensionInfo {
    resources: IBuyboxCommonResources;
    className: string;
    context: ICoreContext<IDimensionsApp>;

    configureErrors: { [configureId: string]: string | undefined };

    getDropdownName(dimensionType: number, resources: IBuyboxCommonResources): string;
    dropdownCallback(notification: IDropdownOnSelectionChangeNotification): Promise<void>;
}

const mapProductDimensionFullToDropdownViewProps = (props: IProductDimensionDropdownProps): IBuyboxProductConfigureDropdownViewProps => {
    const dimensionId = Number.parseInt(props.dropdownId, 10) as ProductDimensionType;
    const dropdownToggleName = format(props.resources.selectDimensionFormatString, props.dropdownName);

    const errorMessage = props.configureErrors[props.dropdownId];

    let dimensionSelectionType = SelectionType.dropdown;

    const dimensionType = convertProductDimensionTypeToDimensionTypes(dimensionId);

    const dimensionsAsSwatchConfiguration = props.context.app.config.dimensionsAsSwatchType;
    if (
        ArrayExtensions.hasElements(dimensionsAsSwatchConfiguration) &&
        !dimensionsAsSwatchConfiguration.includes(DimensionTypes.none) &&
        dimensionsAsSwatchConfiguration.includes(dimensionType)
    ) {
        dimensionSelectionType = SelectionType.swatch;
    }

    return {
        ContainerProps: {
            key: dimensionId,
            className: `${props.className}__dropdown ${props.className}__dimension-${props.dropdownId}`
        },
        LabelContainerProps: {
            tag: 'label',
            className: `${props.className}__dropdown-quantity-label`,
            htmlFor: `${props.className}__dropown-quantity-input-${props.dropdownId}`
        },
        errors: errorMessage && (
            <span className='msc-alert msc-alert-noborder msc-alert-danger' role='alert' aria-live='assertive'>
                <span className='msi-exclamation-triangle' aria-hidden='true' />
                <span>{errorMessage}</span>
            </span>
        ),
        heading: <div>{props.dropdownName}</div>,
        select: (
            <Dropdown
                dropdownId={props.dropdownId}
                dropdownName={props.dropdownName}
                dropdownToggleName={dropdownToggleName}
                dropdownList={props.dropdownList}
                onChange={props.dropdownCallback}
                telemetryContent={telemetryContent}
                selectionType={dimensionSelectionType}
                apiSettings={props.context.request.apiSettings}
            />
        )
    };
};

const getDimensionCombinations = memoize((allDimensions: IDimensionForSelectedVariant[]) => {
    const productIdsToDimensions: Record<number, Record<number, { id: number; value: string }>> = {};
    for (const dimension of allDimensions) {
        for (const dimensionValueWithInventory of dimension.dimensionValuesWithInventory ?? []) {
            for (const productId of dimensionValueWithInventory.ProductIds ?? []) {
                const productIdToDimensions = productIdsToDimensions[productId] ?? (productIdsToDimensions[productId] = {});
                productIdToDimensions[+dimension.DimensionTypeValue] = {
                    id: dimensionValueWithInventory.DimensionValue?.RecordId ?? 0,
                    value: dimensionValueWithInventory.DimensionValue?.Value ?? ''
                };
            }
        }
    }

    const dimensionCombinations = Object.keys(productIdsToDimensions).map(
        productId => productIdsToDimensions[productId] as Record<number, { id: number; value: string }>
    );

    process.env.NODE_ENV === 'development' && console.log('getBuyboxProductConfigure', 'getDimensionCombinations', dimensionCombinations);
    return dimensionCombinations;
});

const getDimensionValuesFromQueryMemoized = memoize((url: string) => getDimensionValuesFromQuery(new URL(url)));

const mapDimensionsInfo = memoize(
    (
        productDimensions: IDimensionForSelectedVariant[],
        matchedDimensions: ProductDimension[],
        enableStockCheck: boolean,
        inventoryLevel: InventoryLevels | undefined,
        getDropdownName: IBuyboxCallbacks['getDropdownName'],
        resources: IBuyboxCommonResources
    ): IProductDimensionInfo[] => {
        const dimensionsInfo = productDimensions.map(productDimensionFull => {
            const dropdownName = getDropdownName(productDimensionFull.DimensionTypeValue, resources);
            const dropdownId = productDimensionFull.DimensionTypeValue.toString();

            const dimensions = (productDimensionFull.dimensionValuesWithInventory ??
                productDimensionFull.DimensionValues ??
                []) as IDimensionValueForSelectedVariant[];
            const validProductDimensionsFull = dimensions.filter(
                value => !ObjectExtensions.isNullOrUndefined(value.DimensionValue?.RecordId ?? value.RecordId)
            );

            const dropdownList: IDropdownItemProps[] = validProductDimensionsFull.map<IDropdownItemProps>(dimensionValuesWithInventory => {
                const dimensionValue = dimensionValuesWithInventory.DimensionValue ?? dimensionValuesWithInventory;
                const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(dimensionValuesWithInventory, inventoryLevel);
                return {
                    value: dimensionValue.Value ?? '',
                    id: dimensionValue.RecordId.toString(),
                    colorHexCode: dimensionValue.ColorHexCode,
                    imageUrl: dimensionValue.ImageUrl,
                    isDisabled: enableStockCheck && inventoryLevelCode === InventoryLevelValues.outOfStock,
                    swatchItemAriaLabel: resources.swatchItemAriaLabel ? resources.swatchItemAriaLabel : ''
                };
            });
            return {
                dropdownName,
                dropdownId,
                dropdownList
            };
        });

        for (const dimensionInfo of dimensionsInfo) {
            const currentDimensionsFromQuery = matchedDimensions.filter(
                dimensionFromQuery => dimensionFromQuery.DimensionTypeValue.toString() === dimensionInfo.dropdownId
            );
            if (!ArrayExtensions.hasElements(currentDimensionsFromQuery)) {
                continue;
            }

            const currentDimensionFromQuery = currentDimensionsFromQuery[0];
            const selectedDimensionItem = dimensionInfo.dropdownList.filter(
                dimensionItem =>
                    dimensionItem.value.toLocaleLowerCase() === currentDimensionFromQuery.DimensionValue?.Value?.toLocaleLowerCase()
            );
            if (!ArrayExtensions.hasElements(selectedDimensionItem)) {
                continue;
            }

            process.env.NODE_ENV === 'development' &&
                console.log('getBuyboxProductConfigure', 'set', selectedDimensionItem[0].id, '=', selectedDimensionItem[0].value);
            selectedDimensionItem[0].isDefault = true;
        }

        const dimensionCombinations = getDimensionCombinations(productDimensions);

        for (const dimensionInfo of dimensionsInfo) {
            const otherDimensionsFromQuery = matchedDimensions.filter(
                dimensionFromQuery => dimensionFromQuery.DimensionTypeValue !== +dimensionInfo.dropdownId
            );

            // valid dimensions where match the other selected values
            const validDimensionValues = dimensionCombinations
                .filter(dc => dc[+dimensionInfo.dropdownId])
                .filter(dc => {
                    const unmatched = otherDimensionsFromQuery.find(
                        od => (dc[od.DimensionTypeValue]?.value.toLowerCase() ?? '') !== (od.DimensionValue?.Value?.toLowerCase() ?? '')
                    );

                    return !unmatched;
                })
                .map(dc => dc[+dimensionInfo.dropdownId]);

            // process.env.NODE_ENV === 'development' &&
            //     console.log(
            //         'getBuyboxProductConfigure',
            //         'when',
            //         otherDimensionsFromQuery
            //             .map(d => `${getDropdownName(d.DimensionTypeValue, resources)}='${d.DimensionValue?.Value}'`)
            //             .join(', '),
            //         dimensionInfo.dropdownName,
            //         'in',
            //         validDimensionValues.map(v => `'${v?.value}'`).join(', ')
            //     );
            dimensionInfo.dropdownList.forEach(dl => {
                if (!validDimensionValues.find(val => val.value === dl.value)) {
                    dl.isDisabled = true;
                    // process.env.NODE_ENV === 'development' && console.log('getBuyboxProductConfigure', 'disable', `'${dl.value}'`);
                }
            });

            // process.env.NODE_ENV === 'development' &&
            //     console.log('getBuyboxProductConfigure', 'mapDimensionsInfo', dimensionInfo, validDimensionCombinations);
        }

        return dimensionsInfo;
    }
);

export function getBuyboxProductConfigure(
    props: IBuyboxExtentedProps<IBuyboxCommonData>,
    state: IBuyboxState,
    callbacks: IHeiBuyboxCallbacks,
    productDetails?: IProductDetails,
    selectedDimensions?: ProductDimension[],
    allDimensions?: IDimensionForSelectedVariant[]
): IBuyboxProductConfigureViewProps | undefined {
    const { resources, typeName } = props;

    const {
        errorState: { configureErrors }
    } = state;

    const { getDropdownName } = callbacks;

    const context = props.context as ICoreContext<IDimensionsApp>;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ? props.data.product.result : productDetails?.product;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const productDimensionsData = allDimensions ?? props.data.productDimensions?.result ?? productDetails?.productDimensions;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = props.context.app.config.enableStockCheck;

    const hasProductDimensions = ArrayExtensions.hasElements(productDimensionsData);

    // process.env.NODE_ENV === 'development' && console.log('getBuyboxProductConfigure', 'dimensionsInfo', product);
    if (!product || !hasProductDimensions) {
        return undefined;
    }

    const className = _getClassNamePrefix(typeName);

    if (!telemetryContent) {
        telemetryContent = getTelemetryObject(context.request.telemetryPageName!, props.typeName, props.telemetry);
    }

    const inventoryLevel = context.app.config.inventoryLevel;

    const productDimensions = productDimensionsData as IDimensionForSelectedVariant[];
    const dimensionCombinations = getDimensionCombinations(productDimensions);
    const matchedDimensions = selectedDimensions ?? getDimensionValuesFromQueryMemoized(context.request.url.requestUrl.toString());

    const onChanged = async (notification: IDropdownOnSelectionChangeNotification) => {
        const possibleDimensions = dimensionCombinations.filter(
            dc => dc[+notification.dropdownId]?.id.toString() === notification.selectId
        );

        const otherDimensions = matchedDimensions.filter(
            dimensionFromQuery => dimensionFromQuery.DimensionTypeValue !== +notification.dropdownId
        );

        const isOtherDimensionsValid = possibleDimensions.find(pd => {
            const unmatched = otherDimensions.find(
                od => (pd[od.DimensionTypeValue]?.value.toLowerCase() ?? '') !== (od.DimensionValue?.Value?.toLowerCase() ?? '')
            );

            return !unmatched;
        });

        const dimensionsSelected: Record<number, string> = {};
        if (!isOtherDimensionsValid && ArrayExtensions.hasElements(possibleDimensions)) {
            const nextDimensions = possibleDimensions[0];
            Object.keys(nextDimensions).forEach(
                dimensionTypeValue => (dimensionsSelected[+dimensionTypeValue] = nextDimensions[+dimensionTypeValue].id.toString())
            );
        } else {
            dimensionsSelected[+notification.dropdownId] = notification.selectId;
        }
        await callbacks.dimensionsSelectedAsync(dimensionsSelected);
    };

    const dimensionsInfo = mapDimensionsInfo(
        productDimensions,
        matchedDimensions,
        enableStockCheck,
        inventoryLevel,
        getDropdownName,
        resources
    );

    // process.env.NODE_ENV === 'development' && console.log('getBuyboxProductConfigure', 'dimensionsInfo', dimensionsInfo);
    const dropdowns: IBuyboxProductConfigureDropdownViewProps[] = dimensionsInfo.map(dimensionInfo => {
        return mapProductDimensionFullToDropdownViewProps({
            ...dimensionInfo,
            resources,
            configureErrors,
            getDropdownName,
            dropdownCallback: onChanged,
            className,
            context
        });
    });

    return {
        ContainerProps: {
            className: `${className}__configure hei-buybox-configure`
        },
        dropdowns
    };
}

const _getClassNamePrefix = (typeName: string): string => {
    return typeName.toLocaleLowerCase() === 'quickview' ? 'ms-quickView' : 'ms-buybox';
};
